import { CircularProgress } from "@material-ui/core";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Table, Dropdown, Modal, Button } from "react-bootstrap";
import SVG from "react-inlinesvg";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toAbsoluteUrl } from "../../../../../_metronic/_helpers";
import Checkbox from "../../../../components/Checkbox";
import BasicPagination from "../../../pagination/BasicPagination";
import { useIsAccessAllow } from "../../../users/redux";
import { showSuccessSnackbar } from "../../../snackBar/snackBar.action";
import Select from "react-select";
import axios from "axios";
import { Tooltip, OverlayTrigger } from "react-bootstrap";
import PerfectScrollbar from "react-perfect-scrollbar";
import "./modernTable.scss";
import CallDetailsModal from "../../components/CallDetailsModal";
import {
  coldLeadsAction,
  deleteColdLeadAsync,
  deleteSelectedColdLeadsAsync,
  getAllColdLeadsAsync,
  startColdLeadCallAsync,
  bulkCallColdLeadsAsync,
} from "../../redux";

// Call Details Button Component with Status Display
const CallDetailsButtonWithStatus = ({
  lead,
  onCallDetailsPageClick,
  getCallStatus,
  getStatusBgColor,
  getStatusTextColor,
  formatDate,
  formatTime,
}) => {
  const [callStatus, setCallStatus] = useState(null);

  useEffect(() => {
    if (lead?._id) {
      getCallStatus(lead._id).then((status) => {
        setCallStatus(status);
      });
    }
  }, [lead?._id, getCallStatus]);

  const tooltip = (
    <Tooltip id={`call-status-${lead?._id}`}>
      <div style={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: "160px", textAlign: "left" }}>
        <div><strong>Date:</strong> {callStatus?.date || "-"}</div>
        <div><strong>Time:</strong> {callStatus?.time || "-"}</div>
        <div><strong>Call Status:</strong> {(callStatus?.status === "Call Concluded" || callStatus?.status === "Not Interested" || callStatus?.status === "Not Concluded") ? "Success" : (callStatus?.status || "Call")}</div>
        <div><strong>Call Outcome:</strong> {callStatus?.outcome || "-"}</div>
      </div>
    </Tooltip>
  );

  const statusBgColor = callStatus ? getStatusBgColor(callStatus.status) : null;
  const statusTextColor = callStatus ? getStatusTextColor(callStatus.status) : null;
  
  // Determine if icon should be white (for dark backgrounds) or dark (for yellow backgrounds)
  // Check the actual background color value - if it's yellow, use dark icon, otherwise use white
  const isYellowBackground = statusBgColor === "#fef3c7" || statusBgColor === "rgb(255, 204, 0)";
  const iconColor = isYellowBackground ? statusTextColor : "#ffffff";

  return (
    <OverlayTrigger placement="top" overlay={tooltip}>
      <button
        className="inline-action-btn inline-status-btn"
        onClick={() => onCallDetailsPageClick(lead)}
        title="Call Details"
        style={{
          backgroundColor: statusBgColor || undefined,
          color: statusTextColor || undefined,
          borderColor: statusBgColor || undefined,
        }}
      >
        <svg viewBox="0 0 640 640" style={{ width: "16px", height: "16px" }}>
          <path d="M320 128C241 128 175.3 185.3 162.3 260.7C171.6 257.7 181.6 256 192 256L208 256C234.5 256 256 277.5 256 304L256 400C256 426.5 234.5 448 208 448L192 448C139 448 96 405 96 352L96 288C96 164.3 196.3 64 320 64C443.7 64 544 164.3 544 288L544 456.1C544 522.4 490.2 576.1 423.9 576.1L336 576L304 576C277.5 576 256 554.5 256 528C256 501.5 277.5 480 304 480L336 480C362.5 480 384 501.5 384 528L384 528L424 528C463.8 528 496 495.8 496 456L496 435.1C481.9 443.3 465.5 447.9 448 447.9L432 447.9C405.5 447.9 384 426.4 384 399.9L384 303.9C384 277.4 405.5 255.9 432 255.9L448 255.9C458.4 255.9 468.3 257.5 477.7 260.6C464.7 185.3 399.1 127.9 320 127.9z" fill={iconColor} />
        </svg>
      </button>
    </OverlayTrigger>
  );
};

// Helper function to get initials from name
const getInitials = (name) => {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Sort indicator component
const SortIndicator = () => (
  <span className="sort-indicator">
    <svg viewBox="0 0 10 10" fill="currentColor">
      <path d="M5 0L3 2h4L5 0z" />
      <path d="M5 10L7 8H3l2 2z" />
    </svg>
  </span>
);

const CandidatesMasterTable = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [checkedVal, setCheckedVal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [recallTextFind, setRecallTextFind] = useState("");
  const [callStatusCache, setCallStatusCache] = useState({}); // Cache for call statuses
  const [openDropdownId, setOpenDropdownId] = useState(null); // Track which dropdown is open
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [callDetailsLeadId, setCallDetailsLeadId] = useState(null);
  const [isBulkCallConfirmOpen, setIsBulkCallConfirmOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // store lead or "bulk"
  const [isStartCallConfirmOpen, setIsStartCallConfirmOpen] = useState(false);
  const [startCallLeadId, setStartCallLeadId] = useState(null);

  const { searchBy, searchText, audioType, isLoading, coldLeadsList, skip, limit: stateLimit, batchFilter, dateFilter, callOutcomeFilter, refreshColdLeadsList, leadType } = useSelector(
    (state) => state.candidates_master,
    shallowEqual
  );
  
  // Ensure default page size is 10
  const limit = stateLimit || 10;
  
  const { user: loggedInUser } = useSelector(
    (state) => state.auth,
    shallowEqual
  );

  const { adminBaseURL } = useSelector(
    (state) => state.environnment.environmentLists,
    shallowEqual
  );

  // Fetch RECALL_TEXT_FIND setting
  // useEffect(() => {
  //   let mounted = true;
  //   (async () => {
  //     try {
  //       const { data } = await axios({
  //         method: "GET",
  //         url: `${process.env.REACT_APP_HOST}${adminBaseURL}/settings`,
  //         headers: { "Content-Type": "application/json" },
  //       });
  //       if (!mounted) return;
  //       if (data?.code === 200 && data?.data?.RECALL_TEXT_FIND) {
  //         setRecallTextFind(data.data.RECALL_TEXT_FIND);
  //       }
  //     } catch (error) {
  //       console.error("Failed to fetch RECALL_TEXT_FIND:", error);
  //     }
  //   })();
  //   return () => {
  //     mounted = false;
  //   };
  // }, [adminBaseURL]);

  const pageSizeOptions = [
    { value: 10, label: "10" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
    { value: 150, label: "150" },
    { value: 200, label: "200" },
  ];

  const defaultPageSize = pageSizeOptions.find((option) => option.value === limit) || pageSizeOptions[0];

  const changePageSize = (option) => {
    dispatch(coldLeadsAction.setLimit(option.value));
    dispatch(coldLeadsAction.setColdLeadsBatchNumber(0));
  };

  // Helper functions for call status (from CallDetailsTabs.js)
  const formatTime = useCallback((ts) => {
    if (!ts) return "-";
    try {
      return new Date(ts).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    } catch (e) {
      return "-";
    }
  }, []);

  const formatDate = useCallback((ts) => {
    if (!ts) return "-";
    try {
      const d = new Date(ts);
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    } catch (e) {
      return "-";
    }
  }, []);

  const normalizeStatusText = useCallback((value) => {
    if (!value || typeof value !== "string") return "";
    const cleaned = value.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
    if (!cleaned) return "";
    return cleaned
      .split(" ")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(" ");
  }, []);

  const getFailureLabel = useCallback((item) => {
    if (!item) return "Call Failed";

    const reason = (item.failure_reason || "").toLowerCase();
    if (reason === "busy") return "Busy Call";
    if (reason === "no-answer" || reason === "no answer") return "No Answer Call";
    if (reason === "canceled" || reason === "cancelled") return "Call Cancel";

    try {
      if (item.call_initiation_failure) {
        const parsed = JSON.parse(item.call_initiation_failure);
        const status =
          parsed?.body?.[0]?.CallStatus || parsed?.body?.[0]?.SipResponseCode;
        if (typeof status === "string") {
          const statusLc = status.toLowerCase();
          if (statusLc.includes("busy")) return "Busy Call";
          if (statusLc.includes("no-answer") || statusLc.includes("no answer"))
            return "Call Not Receive";
          if (statusLc.includes("cancel")) return "Call Cancel";
        }
      }
    } catch (err) {}

    return "Call Failed";
  }, []);

  const getTranscriptSummary = useCallback((item) => {
    if (!item) return "";
    const transcript = item.elevenlabs_call_transcription || item.call_transcript;
    if (!transcript) return "";
    try {
      const parsed = typeof transcript === "string" ? JSON.parse(transcript) : transcript;
      if (parsed && typeof parsed.summary === "string") {
        return parsed.summary;
      }
    } catch (err) {}
    return "";
  }, []);

  const checkAgentMessageForRecallText = useCallback(
    (item, recallText) => {
      if (!item || !recallText || !recallText.trim()) return false;

      const transcript = item.elevenlabs_call_transcription || item.call_transcript;
      if (!transcript) return false;

      try {
        const parsed = typeof transcript === "string" ? JSON.parse(transcript) : transcript;
        const transcriptRows = parsed?.transcript_rows;

        if (Array.isArray(transcriptRows) && transcriptRows.length > 0) {
          const agentMessages = transcriptRows
            .filter((row) => row?.role === "agent" && row?.message)
            .map((row) => (row.message || "").toLowerCase());

          const recallTextLower = recallText.toLowerCase().trim();
          return agentMessages.some((msg) => msg.includes(recallTextLower));
        }
      } catch (err) {
        // Error parsing transcript
      }

      return false;
    },
    []
  );

  const getSuccessDescriptor = useCallback(
    (item) => {
      if (!item) {
        return "Call";
      }

      // Rule 1: Check for no-answer failure
      if (
        item.type === "call_initiation_failure" &&
        (item.failure_reason === "no-answer" || item.failure_reason === "no answer")
      ) {
        return "Not Concluded";
      }

      // Rule 2: Check for voicemail detection
      if (item.voicemail_detection === true) {
        return "Not Concluded";
      }

      // Rule 3: Check elevenlabs_analysis
      if (item.elevenlabs_analysis) {
        try {
          const analysis =
            typeof item.elevenlabs_analysis === "string"
              ? JSON.parse(item.elevenlabs_analysis)
              : item.elevenlabs_analysis;

          const callScheduleEval =
            analysis?.evaluation_criteria_results?.call_schedule_evaluation?.result;
          const clientEngagementEval =
            analysis?.evaluation_criteria_results?.client_engagement_evaluation?.result;
          
          if (callScheduleEval && clientEngagementEval) {
            // Normalize values for comparison (trim whitespace and convert to lowercase)
            const normalizedCallSchedule = String(callScheduleEval).trim().toLowerCase();
            const normalizedClientEngagement = String(clientEngagementEval).trim().toLowerCase();

            // If both evaluations have the same result
            if(normalizedCallSchedule === "success" && normalizedClientEngagement === "success"){
              return "Call Concluded";
            }else if(normalizedCallSchedule === "failure" && normalizedClientEngagement === "failure"){
              return "Not Interested";
            }else if (
              normalizedCallSchedule === "failure" &&
              normalizedClientEngagement === "success"
            ) {
              return "Not Interested";
            } else {
              return "Not Concluded";
            }
          }
        } catch (error) {
          // Error parsing elevenlabs_analysis - continue to fallback
        }
      }

      // Fallback to other status fields
      const summary = getTranscriptSummary(item);
      if (summary) {
        const summaryLc = summary.toLowerCase();
      }

      const message = normalizeStatusText(
        item?.elevenlabs_message || item?.status_message || item?.call_status_message
      );
      if (message) return message;

      const typeDescriptor = normalizeStatusText(item?.type || item?.callOutcome || item?.status);
      if (typeDescriptor) return typeDescriptor;

      return "Call";
    },
    [getTranscriptSummary, normalizeStatusText]
  );

  // Determine if call item is a failure
  const isFailureRecord = useCallback((item) => {
    return (
      item &&
      typeof item.type === "string" &&
      item.type === "call_initiation_failure" &&
      String(item.call_initiation_failure || "").trim() !== ""
    );
  }, []);

  // Get Call Outcome based on status and item
  // const getCallOutcome = useCallback((item, status) => {
  //   if (!item || !status) return "-";

  //   const statusLower = status.toLowerCase();

  //   // Rule 1: If voicemail_detection is true, outcome is "Not Concluded"
  //   if (item.voicemail_detection === true) {
  //     return "Not Concluded";
  //   }

  //   // Rule 2: If status is from failure labels, outcome is "Not Concluded"
  //   const failureStatuses = [
  //     "busy call",
  //     "no answer call",
  //     "call cancel",
  //     "call not receive",
  //     "call failed"
  //   ];
    
  //   if (failureStatuses.includes(statusLower)) {
  //     return "Not Concluded";
  //   }

  //   // Rule 3: If status is from getSuccessDescriptor, outcome is same as status
  //   // This includes: "Call Concluded", "Not Concluded", "Not Interested", or other statuses
  //   // Statuses like "Not Interested" should display as-is in Call Outcome
  //   return status;
  // }, []);

  const getCallOutcome = useCallback((item, status, isFailureRecord) => {
    if (!item || !status) return "-";

    // Priority 1: Use elevenlabs_call_outcome_status from database if available
    // This field comes from the cold_leads_calling_ai table
    if (item.elevenlabs_call_outcome_status) {
      return item.elevenlabs_call_outcome_status;
    }
    if (isFailureRecord) {
      return "Not Concluded";
    }

    return item.elevenlabs_call_outcome_status;
  }, []);
  // Get call status for a lead
  const getCallStatus = useCallback(
    async (leadId) => {
      // Check cache first
      if (callStatusCache[leadId]) {
        return callStatusCache[leadId];
      }

      try {
        const { data } = await axios({
          method: "GET",
          url: `${process.env.REACT_APP_HOST}${adminBaseURL}/candidateMaster/getCallDetails/${leadId}`,
          headers: { "Content-Type": "application/json" },
        });

        if (data?.code === 200 && data?.data) {
          const payload = data.data || {};
          const items = payload.call_ai || payload.callAi || payload.callRecords || [];
          const latestCall = Array.isArray(items) && items.length > 0 ? items[items.length - 1] : null;

          if (latestCall) {
            const isFailure = isFailureRecord(latestCall);
            const status = isFailure
              ? getFailureLabel(latestCall)
              : getSuccessDescriptor(latestCall);
            
            // Get Call Outcome based on status and item
            const outcome = getCallOutcome(latestCall, status);
            const statusData = {
              status,
              outcome,
              date: formatDate(latestCall?.createdOn),
              time: formatTime(latestCall?.createdOn),
              isFailure,
            };
            setCallStatusCache((prev) => ({ ...prev, [leadId]: statusData }));
            return statusData;
          }
        }
      } catch (error) {
        // Failed to fetch call status
      }

      return null;
    },
    [adminBaseURL, formatDate, formatTime, getFailureLabel, getSuccessDescriptor, getCallOutcome, isFailureRecord, callStatusCache]
  );

  // Get status background color
  const getStatusBgColor = useCallback((status) => {
    if (!status) return "#6b7280"; // Dark gray
    const statusLower = status.toLowerCase();
    if (statusLower.includes("success")) return "#10b981"; // Dark green
    if (statusLower.includes("conclude")) return "#10b981"; // Dark green
    if (statusLower.includes("not conclude")) return "#dc2626"; // Dark red
    if (statusLower.includes("busy")) return "#fef3c7"; // Yellow (keep same)
    if (statusLower.includes("no answer") || statusLower.includes("not receive")) return "rgb(255, 204, 0)"; // Yellow/Orange (keep same)
    if (statusLower.includes("cancel")) return "#dc2626"; // Dark red
    if (statusLower.includes("fail")) return "#dc2626"; // Dark red
    if (statusLower.includes("no answer call")) return "rgb(255, 204, 0)"; // Yellow/Orange (keep same)
    return "#3b82f6"; // Dark blue
  }, []);

  // Get status text color
  const getStatusTextColor = useCallback((status) => {
    if (!status) return "#ffffff"; // White for dark gray background
    const statusLower = status.toLowerCase();
    if (statusLower.includes("success")) return "#ffffff"; // White for dark green
    if (statusLower.includes("conclude")) return "#ffffff"; // White for dark green
    if (statusLower.includes("not conclude")) return "#ffffff"; // White for dark red
    if (statusLower.includes("busy")) return "#92400e"; // Dark yellow (keep same for yellow background)
    if (statusLower.includes("no answer") || statusLower.includes("not receive")) return "#ffffff"; // Dark yellow (keep same for yellow background)
    if (statusLower.includes("cancel")) return "#ffffff"; // White for dark red
    if (statusLower.includes("fail")) return "#ffffff"; // White for dark red
    if (statusLower.includes("no answer call")) return "#92400e"; // Dark yellow (keep same for yellow background)
    return "#ffffff"; // White for dark blue
  }, []);

  // Filter leads based on call outcome filter
  const filteredLeads = useMemo(() => {
    if (!coldLeadsList?.records) return [];
    if (!callOutcomeFilter || callOutcomeFilter === "all") {
      return coldLeadsList.records;
    }

    // Filter leads based on cached call status outcomes
    return coldLeadsList.records.filter((lead) => {
      const cachedStatus = callStatusCache[lead._id];
      if (cachedStatus && cachedStatus.outcome) {
        return cachedStatus.outcome === callOutcomeFilter;
      }
      // For leads without cached status, exclude them from filtered results
      // This ensures accurate filtering - we'll fetch their status asynchronously
      return false;
    });
  }, [coldLeadsList?.records, callOutcomeFilter, callStatusCache]);

  // Single source of truth for fetching data - prevents duplicate API calls
  // refreshColdLeadsList is set to true when filters change and false after successful fetch
  useEffect(() => {
    if (refreshColdLeadsList) {
      dispatch(getAllColdLeadsAsync(searchBy, searchText, audioType, batchFilter, dateFilter, leadType));
    }
  }, [dispatch, searchBy, searchText, audioType, batchFilter, dateFilter, skip, limit, leadType, refreshColdLeadsList]);

  // Remove blocked leads from selectedIds when data changes
  useEffect(() => {
    const recordsToCheck = filteredLeads.length > 0 ? filteredLeads : (coldLeadsList?.records || []);
    if (recordsToCheck && selectedIds.length > 0) {
      const blockedIds = recordsToCheck
        .filter((item) => item?.leadType === "Blocked")
        .map((item) => item?._id)
        .filter(Boolean);
      
      if (blockedIds.length > 0) {
        const hasBlockedSelected = selectedIds.some((id) => blockedIds.includes(id));
        if (hasBlockedSelected) {
          setSelectedIds((prev) => prev.filter((id) => !blockedIds.includes(id)));
          if (checkedVal && selectedIds.length === blockedIds.length) {
            setCheckedVal(false);
          }
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredLeads, coldLeadsList]);

  // Log cold leads data when it changes
  useEffect(() => {
    if (coldLeadsList?.records && coldLeadsList.records.length > 0) {
      // Process cold leads data
    }
  }, [coldLeadsList]);

  const onPageChange = (pageIndex) => {
    const nextPage = Math.max(0, pageIndex);
    dispatch(coldLeadsAction.setColdLeadsBatchNumber(nextPage));
  };

  const onViewClick = (data) => {
    history.push(`/candidates-master/view/${data?._id}`);
  };

  const openCallDetailsModal = (data) => {
    if (!data?._id) return;
    setCallDetailsLeadId(data._id);
    setIsCallModalOpen(true);
  };

  const closeCallDetailsModal = () => {
    setIsCallModalOpen(false);
  };

  const onCallDetailsClick = (data) => {
    openCallDetailsModal(data);
  };

  const onCallDetailsPageClick = (data) => {
    if (!data?._id) return;
    window.open(`/candidates-master/call-details/${data._id}`, "_blank");
  };


  const handleResumeClick = (resumeLink, e) => {
    if (e) {
      e.stopPropagation(); // Prevent row click
    }
    if (resumeLink) {
      window.open(resumeLink, "_blank");
    }
  };

  const onStartCallClick = (data) => {
    if (!data?._id) return;
    setStartCallLeadId(data._id);
    setIsStartCallConfirmOpen(true);
  };

  const handleStartCallConfirm = async () => {
    if (!startCallLeadId) return;
    
    const result = await dispatch(startColdLeadCallAsync(startCallLeadId));
    if (result?.success) {
      // Refresh the list after successful call initiation
      dispatch(getAllColdLeadsAsync(searchBy, searchText, audioType, batchFilter, dateFilter, leadType));
    }
    setIsStartCallConfirmOpen(false);
    setStartCallLeadId(null);
  };

  const handleStartCallCancel = () => {
    setIsStartCallConfirmOpen(false);
    setStartCallLeadId(null);
  };

  const onBulkCallClick = () => {
    if (!selectedIds.length) {
      return;
    }

    setIsBulkCallConfirmOpen(true);
  };

  const handleBulkCallConfirm = async () => {
    const result = await dispatch(bulkCallColdLeadsAsync(selectedIds));
    if (result?.success) {
      setCheckedVal(false);
      setSelectedIds([]);
      // Refresh the list after successful bulk call initiation
      dispatch(getAllColdLeadsAsync(searchBy, searchText, audioType, batchFilter, dateFilter, leadType));
    }
    setIsBulkCallConfirmOpen(false);
  };

  const handleBulkCallCancel = () => {
    setIsBulkCallConfirmOpen(false);
  };



  const onDeleteClick = (data) => {
    if (!data?._id) return;
    setDeleteTarget({ type: "single", leadId: data._id });
    setIsDeleteConfirmOpen(true);
  };

  const onDeleteSelected = () => {
    if (!selectedIds.length) return;
    setDeleteTarget({ type: "bulk" });
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!deleteTarget) return;

    if (deleteTarget.type === "single" && deleteTarget.leadId) {
      dispatch(deleteColdLeadAsync(deleteTarget.leadId)).then(() => {
        dispatch(getAllColdLeadsAsync(searchBy, searchText, audioType, batchFilter, dateFilter, leadType));
      });
    } else if (deleteTarget.type === "bulk" && selectedIds.length) {
      dispatch(deleteSelectedColdLeadsAsync(selectedIds)).then(() => {
        setCheckedVal(false);
        setSelectedIds([]);
        dispatch(getAllColdLeadsAsync(searchBy, searchText, audioType, batchFilter, dateFilter, leadType));
      });
    }

    setIsDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  const handleDeleteCancel = () => {
    setIsDeleteConfirmOpen(false);
    setDeleteTarget(null);
  };

  // const onBulkCall = async () => {
  //   if (!selectedIds.length) {
  //     dispatch(showSuccessSnackbar("error", "Please select at least one lead to call.", 3000));
  //     return;
  //   }
    
  //   if (window.confirm(`Are you sure you want to initiate calls for ${selectedIds.length} selected lead(s)?`)) {
  //     const result = await dispatch(bulkCallAsync(selectedIds));
  //     if (result?.success) {
  //       setCheckedVal(false);
  //       setSelectedIds([]);
  //       dispatch(getAllColdLeadsAsync(searchBy, searchText, audioType, batchName, sortColumn, sortDirection));
  //     }
  //   }
  // };

  const handleSelectAll = () => {
    setCheckedVal(!checkedVal);
    if (!checkedVal) {
      // Only select non-blocked leads from filtered results
      const recordsToUse = filteredLeads.length > 0 ? filteredLeads : (coldLeadsList?.records || []);
      const nextIds =
        recordsToUse
          ?.filter((item) => item?.leadType !== "Blocked")
          ?.map((item) => item?._id)
          .filter(Boolean) || [];
      setSelectedIds(nextIds);
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (event) => {
    const { id, checked } = event.target;
    // Find the lead to check if it's blocked
    const lead = coldLeadsList?.records?.find((item) => item?._id === id);
    if (lead?.leadType === "Blocked") {
      // Prevent selection of blocked leads
      event.preventDefault();
      return;
    }
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((item) => item !== id));
    }
  };


  const getLeadTypeStyle = (leadType) => {
    switch (leadType?.toLowerCase()) {
      case "unblock":
        return {
          color: "#28a745",
          fontWeight: "500",
        };
      case "blocked":
        return {
          color: "#dc3545",
          fontWeight: "500",
        };
      default:
        return {
          color: "#6c757d",
          fontWeight: "500",
        };
    }
  };
  const formatLeadType = (leadType) => {
    if (!leadType) return "-";
    const formatted = leadType.charAt(0).toUpperCase() + leadType.slice(1).toLowerCase();
    return formatted;
  };


  const hasSelection = selectedIds.length > 0;

  const isViewAllow = useIsAccessAllow("candidatemaster.view");
  const isDeleteAllow = useIsAccessAllow("candidatemaster.delete");
  const isBulkDeleteAllow = useIsAccessAllow("candidatemaster.multiple_delete");
  const isCallDetailsAllow = useIsAccessAllow("candidatemaster.view");
  const isInitiateCallAllow = useIsAccessAllow("candidatemaster.initiate_call");
  const isBulkCallAllow = useIsAccessAllow("candidatemaster.initiate_call");


  // Fetch call statuses for all leads when call outcome filter is active
  useEffect(() => {
    if (callOutcomeFilter && callOutcomeFilter !== "all" && coldLeadsList?.records) {
      // Fetch call statuses for leads that don't have cached status
      const leadsToFetch = coldLeadsList.records.filter(
        (lead) =>
          !callStatusCache[lead._id] &&
          lead?.elevenlabs_call_ai_id &&
          lead?.elevenlabs_conversation_id
      );

      // Fetch call statuses in batches to avoid overwhelming the API
      const fetchBatch = async (leads) => {
        for (const lead of leads) {
          try {
            await getCallStatus(lead._id);
          } catch (error) {
            // Silently fail for individual leads
          }
        }
      };

      if (leadsToFetch.length > 0) {
        fetchBatch(leadsToFetch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callOutcomeFilter, coldLeadsList?.records]);

  const totalRecords = callOutcomeFilter && callOutcomeFilter !== "all" 
    ? filteredLeads.length 
    : (coldLeadsList?.recordsTotal || 0);
  const totalPages = Math.ceil(totalRecords / (limit || 1));
  const currentPage = skip + 1;

  return (
    <>
    <div className="px-6 pt-0 dash_wt_bx pb-0 ovflhddn loader-display-position" style={{ background: "#ffffff", borderRadius: "12px", padding: "24px", display: "flex", flexDirection: "column", height: "calc(100vh - 120px)", minHeight: "700px" }}>
      <div className="tblmrgn mt-0" style={{ display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0 }}>
        {(isBulkDeleteAllow || isBulkCallAllow) && (
          <div className="mt-4 mb-4" style={{ flex: "0 0 auto" }}>
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              {isBulkCallAllow && (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="bulk-call-tooltip">Bulk Call</Tooltip>}
                >
                  <button 
                    type="button" 
                    className="custom-action-button button-success"
                    disabled={!hasSelection} 
                    onClick={onBulkCallClick}
                    style={{ minWidth: "40px", padding: "8px" }}
                  >
                    <span className="svg-icon" style={{ color: "#ffffff" }}>
                      <SVG src={toAbsoluteUrl("/media/svg/icons/custom/Call.svg")} height={20} width={20} />
                    </span>
                  </button>
                </OverlayTrigger>
              )}
              {isBulkDeleteAllow && (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="bulk-delete-tooltip">Delete</Tooltip>}
                >
                  <button 
                    type="button" 
                    className="custom-action-button button-danger"
                    disabled={!hasSelection} 
                    onClick={onDeleteSelected}
                    style={{ minWidth: "40px", padding: "8px" }}
                  >
                    <span className="svg-icon" style={{ color: "#ffffff" }}>
                      <SVG src={toAbsoluteUrl("/media/svg/icons/custom/delete.svg")} height={20} width={20} />
                    </span>
                  </button>
                </OverlayTrigger>
              )}
            </div>
          </div>
        )}
        <div className="table-container-wrapper" style={{ flex: "1 1 auto", minHeight: 0, height: "100%" }}>
          <PerfectScrollbar
            options={{
              wheelSpeed: 2,
              wheelPropagation: false,
            }}
            className="scroll"
            style={{
              flex: 1,
              minHeight: 0,
            }}
          >
            <Table hover className="mb-0 modern-table">
              <thead>
                <tr>
                  {isBulkDeleteAllow && (
                    <th style={{ width: "50px", minWidth: "50px", maxWidth: "50px", whiteSpace: "nowrap" }}>
                      <label className="custom-checkbox-wrapper">
                        <Checkbox
                          type="checkbox"
                          name="checkAll"
                          id="checkAll"
                          handleClick={handleSelectAll}
                          isChecked={checkedVal}
                        />
                        <span className="custom-checkbox"></span>
                      </label>
                    </th>
                  )}
                  <th style={{ width: "70px", minWidth: "70px", maxWidth: "70px", whiteSpace: "nowrap" }}>
                    Sr No.
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>
                    Batch
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>
                    Name
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>
                    Contact Number
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>
                    Technology
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>
                    Experience
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>
                    Education
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>
                    Hiring Location
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>
                    Current Location &amp; Time
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>
                    Created By
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>
                    Updated On
                  </th>
                  <th style={{ whiteSpace: "nowrap" }}>Lead Type</th>
                  <th style={{ whiteSpace: "nowrap", width: "220px", minWidth: "220px", textAlign: "center"}}>Action</th>
                </tr>
              </thead>
              <tbody>
                {!isLoading && filteredLeads && filteredLeads.length ? (
                  filteredLeads.map((lead, index) => {
                    // Extract created by user name from various possible field structures
                    const createdByName = lead?.createdByName || lead?.created_by_name || "-";
                    // Consider call details available when the call detail button would render
                    const hasCallDetails = Boolean(lead?.elevenlabs_call_ai_id && lead?.elevenlabs_conversation_id);
                    // Check if lead is blocked
                    const isBlocked = lead?.leadType === "Blocked";
                    return (
                    <tr 
                      key={lead?._id || index}
                      style={{
                        opacity: isBlocked ? 0.6 : 1,
                        cursor: isBlocked ? "not-allowed" : "default",
                      }}
                      onClick={(e) => {
                        // Prevent row selection when blocked
                        if (isBlocked) {
                          e.stopPropagation();
                        }
                      }}
                    >
                      {isBulkDeleteAllow && (
                        <td style={{ width: "50px", minWidth: "50px", maxWidth: "50px", whiteSpace: "nowrap" }}>
                          <label 
                            className="custom-checkbox-wrapper"
                            style={{
                              cursor: isBlocked ? "not-allowed" : "pointer",
                              opacity: isBlocked ? 0.5 : 1,
                            }}
                            onClick={(e) => {
                              if (isBlocked) {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }}
                          >
                            <Checkbox
                              type="checkbox"
                              name={lead?._id}
                              id={lead?._id}
                              handleClick={(e) => {
                                if (!isBlocked) {
                                  handleSelectOne(e);
                                }
                              }}
                              isChecked={selectedIds.includes(lead?._id)}
                              disabled={isBlocked}
                            />
                            <span className="custom-checkbox"></span>
                          </label>
                        </td>
                      )}
                      <td style={{ width: "70px", minWidth: "70px", maxWidth: "70px", whiteSpace: "nowrap" }}>
                        <span>{skip * limit + (index + 1)}</span>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {lead?.batchName ? (
                          <span className="table-badge" style={{ background: "#E1E9FF", color: "#6993FF" }}>
                            {lead.batchName}
                          </span>
                        ) : (
                          <span>{lead?.batch_name || "-"}</span>
                        )}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          {isCallDetailsAllow && hasCallDetails ? (
                            <button
                              type="button"
                              onClick={() => onCallDetailsClick(lead)}
                              className="profile-name clickable-name"
                              style={{
                                background: "none",
                                border: "none",
                                padding: 0,
                                margin: 0,
                                color: "#3b82f6",
                                textDecoration: "underline",
                                cursor: "pointer",
                                fontSize: "inherit",
                                fontWeight: "inherit",
                              }}
                            >
                              {lead?.name || "-"}
                            </button>
                          ) : (
                            <span className="profile-name">{lead?.name || "-"}</span>
                          )}
                          {lead?.email && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`email-tooltip-${lead?._id || index}`}>
                                  {lead.email}
                                </Tooltip>
                              }
                            >
                              <span
                                className="svg-icon svg-icon-primary"
                                style={{
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.location.href = `mailto:${lead.email}`;
                                }}
                              >
                                <SVG src={toAbsoluteUrl("/media/svg/icons/Communication/Mail.svg")} height={16} width={16} />
                              </span>
                            </OverlayTrigger>
                          )}
                        </div>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <span className="f-w-500">{lead?.contactNumber || "-"}</span>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {lead?.technology && Array.isArray(lead.technology) && lead.technology.length > 0 ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
                            {lead.technology.map((tech, idx) => (
                              <span
                                key={idx}
                                className="table-badge"
                                style={{ background: "#E1E9FF", color: "#6993FF" }}
                              >
                                {tech}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {lead?.experience_years !== undefined && lead?.experience_years !== null ? (
                            <span>{lead.experience_years} {lead.experience_years === 1 ? "year" : "years"}</span>
                          ) : (
                            <span>-</span>
                          )}
                          {lead?.resume_link && (
                          <OverlayTrigger
                            placement="top"
                              overlay={<Tooltip id={`resume-tooltip-${lead?._id || index}`}>Open Resume</Tooltip>}
                          >
                            <span
                                className="svg-icon svg-icon-primary"
                              style={{
                                  cursor: "pointer",
                                  display: "inline-flex",
                                  alignItems: "center",
                              }}
                                onClick={(e) => handleResumeClick(lead.resume_link, e)}
                              >
                                <SVG src={toAbsoluteUrl("/media/svg/icons/Files/File-download.svg")} height={18} width={18} />
                            </span>
                          </OverlayTrigger>
                        )}
                        </div>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <span>{lead?.education || "-"}</span>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <span>{lead?.hiring_location || "-"}</span>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <span>{lead?.current_location || "-"}</span>
                      </td>
                      {/* <td style={{ whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span className="f-w-500">{lead?.countryName || "-"} 
                            <br/>{lead?.timezone ? lead?.timezone : " -"}</span>
                          <span className="f-w-500" style={{ fontSize: "0.875rem", color: "#6b7280" }}>
                            {lead?.currentCountryTime || "-"}
                            
                          </span>
                        </div>
                      </td> */}
                     
                      <td style={{ whiteSpace: "nowrap" }}>
                        <span>{createdByName || "-"}</span>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <span>
                          {lead?.updatedOn || lead?.updated_on
                            ? (() => {
                                const d = new Date(lead.updatedOn || lead.updated_on);
                                const dd = String(d.getDate()).padStart(2, "0");
                                const mm = String(d.getMonth() + 1).padStart(2, "0");
                                const yy = String(d.getFullYear()).slice(-2);
                                const time = d.toLocaleTimeString(undefined, {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                });
                                return `${dd}/${mm}/${yy} ${time}`;
                              })()
                            : lead?.createdOn
                            ? (() => {
                                const d = new Date(lead.createdOn);
                                const dd = String(d.getDate()).padStart(2, "0");
                                const mm = String(d.getMonth() + 1).padStart(2, "0");
                                const yy = String(d.getFullYear()).slice(-2);
                                const time = d.toLocaleTimeString(undefined, {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  hour12: true,
                                });
                                return `${dd}/${mm}/${yy} ${time}`;
                              })()
                            : "-"}
                        </span>
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        {lead?.leadType ? (
                          <span style={getLeadTypeStyle(lead?.leadType)}>
                            {formatLeadType(lead?.leadType)}
                          </span>
                        ) : (
                          <span>{lead?.leadType || "-"}</span>
                        )}
                      </td>
                      <td style={{ whiteSpace: "nowrap", textAlign: "right", width: "220px", minWidth: "220px" }}>
                        <div className="inline-actions">
                          {isCallDetailsAllow && lead?.elevenlabs_call_ai_id && lead?.elevenlabs_conversation_id && (
                            <CallDetailsButtonWithStatus
                              lead={lead}
                              onCallDetailsPageClick={onCallDetailsPageClick}
                              getCallStatus={getCallStatus}
                              getStatusBgColor={getStatusBgColor}
                              getStatusTextColor={getStatusTextColor}
                              formatDate={formatDate}
                              formatTime={formatTime}
                            />
                          )}
                          {isInitiateCallAllow && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`start-call-tooltip-${lead?._id || index}`}>
                                  {isBlocked ? "This lead is blocked" : "Start Call"}
                                </Tooltip>
                              }
                            >
                              <button
                                type="button"
                                className="inline-action-btn"
                                onClick={() => {
                                  if (!isBlocked) {
                                    onStartCallClick(lead);
                                  }
                                }}
                                disabled={isBlocked}
                                style={{
                                  opacity: isBlocked ? 0.5 : 1,
                                  cursor: isBlocked ? "not-allowed" : "pointer",
                                }}
                              >
                                <span className="svg-icon">
                                  <SVG src={toAbsoluteUrl("/media/svg/icons/custom/call-flat.svg")} />
                                </span>
                              </button>
                            </OverlayTrigger>
                          )}
                          {isViewAllow && (
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip id={`view-tooltip-${lead?._id || index}`}>View Details</Tooltip>}
                            >
                              <button
                                type="button"
                                className="inline-action-btn"
                                onClick={() => onViewClick(lead)}
                              >
                                <span className="svg-icon svg-icon-primary">
                                  <SVG src={toAbsoluteUrl("/media/svg/icons/custom/eye.svg")} />
                                </span>
                              </button>
                            </OverlayTrigger>
                          )}
                          {isDeleteAllow && (
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`delete-tooltip-${lead?._id || index}`}>
                                  {isBlocked ? "This lead is blocked" : "Delete Lead"}
                                </Tooltip>
                              }
                            >
                              <button
                                type="button"
                                className="inline-action-btn danger"
                                onClick={() => {
                                  if (!isBlocked) {
                                    onDeleteClick(lead);
                                  }
                                }}
                                disabled={isBlocked}
                                style={{
                                  opacity: isBlocked ? 0.5 : 1,
                                  cursor: isBlocked ? "not-allowed" : "pointer",
                                }}
                              >
                                <span className="svg-icon svg-icon-danger">
                                  <SVG src={toAbsoluteUrl("/media/svg/icons/custom/delete.svg")} height={20} width={20} />
                                </span>
                              </button>
                            </OverlayTrigger>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })
                ) : (
                  <tr>
                  <td colSpan={isBulkDeleteAllow ? 14 : 13} className="text-center">
                    {isLoading ? "Loading..." : callOutcomeFilter && callOutcomeFilter !== "all" && coldLeadsList?.records && coldLeadsList.records.length > 0 
                      ? "No leads match the selected call outcome filter. Call statuses are being fetched..." 
                      : "No Record Found"}
                  </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </PerfectScrollbar>
        </div>
        {!isLoading && (
          <div className="modern-pagination" style={{ flex: "0 0 auto" }}>
              <div className="pagination-info" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span style={{ fontSize: "14px", color: "#6b7280" }}>Show</span>
                  <Select
                    classNamePrefix="react-select"
                    value={defaultPageSize}
                    options={pageSizeOptions}
                    onChange={changePageSize}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    styles={{
                      container: (base) => ({
                        ...base,
                        width: 90,
                      }),
                      control: (base, state) => ({
                        ...base,
                        minHeight: 36,
                        height: 36,
                        borderColor: state.isFocused ? "#6993FF" : "#d1d5db",
                        boxShadow: state.isFocused ? "0 0 0 3px rgba(105, 147, 255, 0.1)" : "none",
                        "&:hover": {
                          borderColor: "#6993FF",
                        },
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        padding: "0 10px",
                        height: 36,
                        display: "flex",
                        alignItems: "center",
                      }),
                      singleValue: (base) => ({
                        ...base,
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#111827",
                      }),
                      indicatorSeparator: () => ({ display: "none" }),
                      dropdownIndicator: (base, state) => ({
                        ...base,
                        padding: 6,
                        color: state.isFocused ? "#6993FF" : "#6b7280",
                      }),
                      option: (base, state) => ({
                        ...base,
                        fontSize: 13,
                        padding: "8px 10px",
                        backgroundColor: state.isSelected
                          ? "#6993FF"
                          : state.isFocused
                          ? "#f3f4f6"
                          : "#fff",
                        color: state.isSelected ? "#fff" : "#111827",
                      }),
                      menuPortal: (base) => ({
                        ...base,
                        zIndex: 9999,
                      }),
                    }}
                  />
                </div>
                Showing {filteredLeads && filteredLeads.length ? skip * limit + 1 : 0} to{" "}
                {filteredLeads && filteredLeads.length ? Math.min(skip * limit + filteredLeads.length, totalRecords) : 0} of {totalRecords} entries
              </div>
              <div className="pagination-controls">
                <button className="page-btn" onClick={() => onPageChange(skip - 1)} disabled={skip === 0}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
                {(() => {
                  const pages = [];
                  if (totalPages > 0) pages.push(1);
                  for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                    if (!pages.includes(i)) pages.push(i);
                  }
                  if (totalPages > 1 && !pages.includes(totalPages)) pages.push(totalPages);
                  return pages.map((page, idx) => {
                    const showEllipsisBefore = idx > 0 && page - pages[idx - 1] > 1;
                    return (
                      <React.Fragment key={page}>
                        {showEllipsisBefore && <span style={{ padding: "0 4px" }}>...</span>}
                        <button className={`page-btn ${currentPage === page ? "active" : ""}`} onClick={() => onPageChange(page - 1)}>
                          {page}
                        </button>
                      </React.Fragment>
                    );
                  });
                })()}
                <button
                  className="page-btn"
                  onClick={() => onPageChange(skip + 1)}
                  disabled={(skip + 1) * limit >= totalRecords}
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        {isLoading && (
          <div style={{ flex: "0 0 auto" }}>
            <div style={{ justifyContent: "center" }} className="text-center">
              <div className="basic-verification-loader text-center">
                <CircularProgress />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    <Modal
      show={isBulkCallConfirmOpen}
      onHide={handleBulkCallCancel}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Confirm Bulk Call</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {`Are you sure you want to initiate calls for ${selectedIds.length} selected lead(s)? This will call each lead and retry once if the first call is not concluded.`}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleBulkCallCancel}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleBulkCallConfirm}>
          OK
        </Button>
      </Modal.Footer>
    </Modal>
    <Modal
      show={isDeleteConfirmOpen}
      onHide={handleDeleteCancel}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Confirm Delete</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {deleteTarget?.type === "bulk"
          ? `Are you sure you want to delete ${selectedIds.length} selected cold lead(s)?`
          : "Are you sure you want to delete this lead?"}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleDeleteCancel}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleDeleteConfirm}>
          OK
        </Button>
      </Modal.Footer>
    </Modal>
    <Modal
      show={isStartCallConfirmOpen}
      onHide={handleStartCallCancel}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Confirm Call</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        Are you sure you want to initiate a call for this lead?
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleStartCallCancel}>
          Cancel
        </Button>
        <Button variant="success" onClick={handleStartCallConfirm}>
          OK
        </Button>
      </Modal.Footer>
    </Modal>
    <CallDetailsModal
      show={isCallModalOpen}
      onHide={closeCallDetailsModal}
      leadId={callDetailsLeadId}
    />
    </>
  );
};

export default CandidatesMasterTable;

